/**
 * Music Metadata Types
 * Core metadata schema for music tracks with DDEX extension support
 */

import { INGESTION_CONFIG } from '@/core/config/ingestion';
import type { CatalogImportSession, MusicRelationship, RightsIntelligenceReport, SongIntake, VideoMusicIdentity } from '@indii/shared';

export interface MasterAudioReference {
    audioProperties?: {
        bitDepth: number;
        channels: number;
        codec: 'PCM' | 'FLAC';
        container: 'wav' | 'flac';
        sampleRate: number;
    };
    contentHash: string;
    downloadUrl: string;
    /** Immutable Cloud Storage generation profiled by the server worker. Legacy references may lack it. */
    generation?: string;
    masterFingerprint: string;
    mimeType: string;
    originalFileName: string;
    sizeBytes: number;
    storagePath: string;
    uploadedAt: string;
}

export interface RoyaltySplit {
    legalName: string;
    role: 'songwriter' | 'producer' | 'performer' | 'other';
    percentage: number; // 0-100
    email: string; // For rapid payment
    isni?: string; // International Standard Name Identifier
    spotifyId?: string; // DSP URI
    appleMusicId?: string; // DSP URI
    distributorId?: string; // For platform-specific routing (e.g. 'youtube')
}

export interface GoldenMetadata {
    // 1. Core Identity
    trackTitle: string;
    artistName: string;
    isrc: string; // International Standard Recording Code
    iswc?: string; // International Standard Musical Work Code (Optional but recommended)
    explicit: boolean;
    genre: string;
    labelName: string;
    dpid?: string; // DDEX Party ID

    // 2. The Economics
    splits: RoyaltySplit[];
    /** Composition ownership/writer shares used for PRO, MLC, and publishing flows. */
    compositionSplits?: RoyaltySplit[];
    /** Sound-recording/master ownership shares used for recording revenue flows. */
    recordingSplits?: RoyaltySplit[];

    // 3. Rights Administration
    pro: 'ASCAP' | 'BMI' | 'SESAC' | 'GMR' | 'None';
    publisher: string;

    // 4. Content Content & Clearance
    containsSamples: boolean;
    masterFingerprint?: string; // The "Sonic ID" of the track itself
    samples?: {
        fingerprint?: string; // Unique ID of the sample file
        sourceName: string; // e.g. "Splice: Funky Drum Loop 001"
        cleared: boolean;
        licenseFile?: string; // Path or URL to license PDF
        clearanceDetails?: {
            licenseType: string;
            termsSummary: string;
            platformId?: string;
        };
    }[];
    lyrics?: string;

    // 5. Verification
    isGolden: boolean; // Computed flag: true only if schema is valid and splits sum to 100%
}

/**
 * Extended GoldenMetadata with DDEX-specific fields
 * For distribution and rights management
 */
export interface ExtendedGoldenMetadata extends GoldenMetadata {
    // Internal Identifier
    id?: string;
    userId?: string;
    masterAsset?: MasterAudioReference;
    /** Phase 4 progressive intake state; identifiers remain evidence, never identity. */
    songIntake?: SongIntake;
    /** Canonical music relationships confirmed during intake. */
    musicRelationships?: MusicRelationship[];
    /** Canonical VideoResource/Asset lineage created by the existing private render flow. */
    videoMusicIdentities?: VideoMusicIdentity[];
    /** Phase 5 non-destructive existing-catalog reconciliation state. */
    catalogImport?: CatalogImportSession;
    /** Phase 6 deterministic rights preflight; never substitutes for legal review. */
    rightsIntelligence?: RightsIntelligenceReport;
    /** Measured properties of the immutable master used in DDEX technical details. */
    audioTechnical?: {
        bitDepth: number;
        channels: number;
        codec: 'PCM' | 'FLAC';
        container: 'wav' | 'flac';
        sampleRate: number;
    };

    // Artist Disambiguation
    artistIsni?: string;
    artistSpotifyId?: string;
    artistAppleMusicId?: string;

    // Release Information
    tracks?: GoldenMetadata[];
    releaseType: 'Single' | 'EP' | 'Album' | 'Compilation';
    releaseDate: string; // ISO 8601 date
    preOrderDate?: string;
    originalReleaseDate?: string;

    // Distribution
    territories: string[]; // ISO 3166-1 codes or 'Worldwide'
    distributionChannels: ('streaming' | 'download' | 'physical')[];
    exclusiveTerritory?: string;
    exclusiveEndDate?: string;

    // Additional Identifiers
    upc?: string; // Universal Product Code (for albums/EPs)
    gridId?: string; // Global Release Identifier
    catalogNumber?: string;
    releaseTitle?: string; // Defaults to trackTitle for Singles

    // Copyright Information
    pLineYear?: number;
    pLineText?: string; // e.g., "2025 Artist Name"
    cLineYear?: number;
    cLineText?: string; // e.g., "2025 Publisher Name"

    // Intelligence Content Disclosure (ERN 4.3)
    aiGeneratedContent: {
        isFullyAIGenerated: boolean;
        isPartiallyAIGenerated: boolean;
        aiToolsUsed?: string[];
        humanContribution?: string;
    };

    // Enhanced Metadata for Discovery
    subGenre?: string;
    mood?: string[];
    keywords?: string[];
    language?: string; // ISO 639-2 (e.g., 'eng', 'spa')
    isInstrumental?: boolean;
    bpm?: number;
    key?: string;
    energy?: number;
    immersiveAudioProfile?: 'DolbyAtmos' | 'Sony360' | 'None';

    // Marketing
    marketingComment?: string;
    focusTrack?: boolean; // Highlight for playlist pitching

    // Duration (computed from audio)
    durationSeconds?: number;
    durationFormatted?: string; // "3:45"
    durationDDEXFormatted?: string; // e.g. "PT3M4S.123"

    // Confidence Scores
    confidenceScores?: {
        genre?: number;
        mood?: number;
        danceability?: number;
        [key: string]: number | undefined;
    };

    // Rights & Publishing (PRO Registration)
    composerName?: string; // Composer legal name (may differ from artistName)
    composerIPI?: string; // Interested Parties Information number
    publisherName?: string; // Publisher entity name
    publisherShare?: number; // Publisher ownership percentage (0-100)
    isCoverSong?: boolean; // Requires mechanical license if true
    originalSongTitle?: string; // Title of the original work (for cover songs)

    // YouTube Content ID (Item 233)
    youtubeContentIdOptIn?: boolean; // When true, content policy deal is included in DDEX ERN delivery
    youtubeContentIdPolicy?: 'monetize' | 'track' | 'block'; // Default: 'monetize'

    // Cover Art Intelligence Disclosure (2026 DSP Compliance)
    coverArtAIGenerated?: boolean; // true when cover art was created by Autonomous (e.g., Nano Banana)
}

// Type for release status in distribution
export type ReleaseDistributionStatus =
    | 'draft'
    | 'metadata_complete'
    | 'assets_uploaded'
    | 'validating'
    | 'pending_review'
    | 'approved'
    | 'delivering'
    | 'live'
    | 'takedown_requested'
    | 'taken_down'
    // ISSUE-964: the draft record can be created successfully while the
    // Publishing agent's definitive packaging step fails — this state
    // makes that distinction reloadable/retryable instead of silently
    // advancing to 'metadata_complete' regardless of packaging outcome.
    | 'packaging_failed';

// Release record for Firestore
export interface DDEXReleaseRecord {
    id: string;
    orgId: string;
    projectId: string;
    userId: string;

    // Metadata
    metadata: ExtendedGoldenMetadata;

    // Assets
    assets: {
        audioUrl: string;
        audioFormat: 'wav' | 'flac' | 'mp3';
        audioSampleRate: number;
        audioBitDepth: number;
        audioStoragePath?: string;
        audioContentHash?: string;
        masterFingerprint?: string;
        isrc?: string;
        coverArtUrl: string;
        coverArtWidth: number;
        coverArtHeight: number;
    };

    // Distribution State
    status: ReleaseDistributionStatus;
    packagingError?: string; // set when status === 'packaging_failed'
    distributors: {
        distributorId: string;
        releaseId?: string;
        status: string;
        submittedAt?: string;
        publishedAt?: string;
        error?: string;
    }[];

    // Timestamps
    createdAt: string;
    updatedAt: string;
    submittedAt?: string;
    publishedAt?: string;
}

export const INITIAL_METADATA: ExtendedGoldenMetadata = {
    trackTitle: '',
    artistName: '',
    isrc: '',
    explicit: false,
    genre: '',
    // Getters, not eager values: INGESTION_CONFIG throws when the ingestion env
    // vars are absent (fail-closed by design). Getters defer that check to the
    // point of use (spread/read) instead of crashing the app at import time.
    get labelName() { return INGESTION_CONFIG.ENTITY_NAME; },
    get dpid() { return INGESTION_CONFIG.SYSTEM_IDENTIFIER; },
    splits: [],
    pro: 'None',
    publisher: 'Self-Published',
    containsSamples: false,
    samples: [],
    isGolden: false,
    releaseType: 'Single',
    releaseDate: new Date().toISOString().split('T')[0]!,
    territories: ['Worldwide'],
    distributionChannels: ['streaming', 'download'],
    aiGeneratedContent: {
        isFullyAIGenerated: false,
        isPartiallyAIGenerated: false
    },
    youtubeContentIdOptIn: false,
    youtubeContentIdPolicy: 'monetize'
};
